import { useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { ExportMenu } from '../../accounting/ExportMenu';
import { api } from '../../../api/client';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow, useConfirm } from '../../../components/confirm';
import { useStates, useSaveState, useStateCountries, errMessage, type StateRow } from './systemMasters';
import { useContentRectStyle } from './useContentRectStyle';
import { StateCitiesPanel } from './StateCitiesPanel';
import type { StagedCity, StagedState } from './city';

interface Props {
  /** Saved Country Oid (server mode). Absent ⇒ a new, unsaved country (staged mode). */
  countryId?: string;
  /** States staged in memory for a new country (committed when the country is saved). */
  staged?: StagedState[];
  onStaged?: (next: StagedState[]) => void;
}

const newKey = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `k${Date.now()}-${Math.round(Math.random() * 1e6)}`);

/**
 * The State child grid inside the Country detail/edit form — mirrors the legacy
 * `CustomCountry_DetailView ▸ State` nested list. Country → State is one-to-many
 * (CustomState.Country FK), so the toolbar offers New (create under this
 * country), Link (re-assign an existing state to it), Unlink (clear its
 * country — the state is kept) and Edit. Server mode (saved country) commits
 * immediately; staged mode (new country) keeps New/Link/Unlink/Edit usable in
 * memory and commits the whole chain when the country is saved.
 */
export function CountryStatesPanel({ countryId, staged = [], onStaged }: Props) {
  const staging = !countryId;
  const noCountry = !countryId && !onStaged;
  const notify = useNotify();
  const runSave = useSaveFlow();
  const { confirm } = useConfirm();
  const { data: allStates = [], isFetching } = useStates();
  const saveState = useSaveState();

  const mine = countryId ? allStates.filter((s) => s.countryId === countryId) : [];
  const rows: StateRow[] = staging
    ? staged.map((s) => ({ id: s.key, longName: s.name, shortName: s.shortName ?? null, countryId: null, country: null }))
    : mine;
  // GoLive's Link lookup lists the FULL State master (a new state is persisted on
  // create, so it is a real dbo.CustomState row), so both modes show allStates.
  const linkable: StateRow[] = allStates;
  /** The real dbo.CustomState Oid behind a grid row — the row id IS the Oid in
   *  server mode, but a staged KEY on a new country. */
  const realStateId = (rowId: string): string =>
    staging ? (staged.find((s) => s.key === rowId)?.stateId ?? rowId) : rowId;
  /** State Oids ALREADY on this country — GoLive DISABLES the Link picker's edit
   *  pencil for these (they're edited from the grid, not the lookup). */
  const linkedStateIds = new Set(
    (staging ? staged.map((s) => s.stateId).filter(Boolean) as string[] : mine.map((s) => s.id))
      .map((id) => id.toLowerCase()),
  );
  const isLinked = (id: string) => linkedStateIds.has(id.toLowerCase());
  /** Preserve a state's existing Country when renaming (the PUT is a full replace). */
  const countryOf = (sid: string): string | null =>
    allStates.find((s) => s.id.toLowerCase() === sid.toLowerCase())?.countryId ?? null;

  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [dlg, setDlg] = useState<StateRow | null | undefined>(undefined); // undefined=closed · null=new · row=edit
  const [sLong, setSLong] = useState('');
  const [sShort, setSShort] = useState('');
  const [sErr, setSErr] = useState('');
  const [sCountry, setSCountry] = useState('');   // State popup's Country lookup (LIMS CustomState.Country)
  // LIMS shows the Country field only when the State detail is opened STANDALONE
  // (from the link-picker pencil); when opened from this country's nested grid the
  // country is implied by the parent, so it's hidden.
  const [dlgShowCountry, setDlgShowCountry] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkSel, setLinkSel] = useState<Set<string | number>>(new Set());
  const [linkSearch, setLinkSearch] = useState('');   // Link-popup search box (LIMS auto-filter)
  const [busy, setBusy] = useState(false);
  const [popupMax, setPopupMax] = useState(false);   // nested popup opens at normal centred size; toggle to maximize
  const popupMaxStyle = useContentRectStyle(popupMax && (dlg !== undefined || linkOpen));
  // Cities staged in memory while adding a brand-new state (committed on save).
  const [stagedCities, setStagedCities] = useState<StagedCity[]>([]);
  // Inline row-edit (1st pencil) — State + ShortName in place, like the master grids.
  const [inlineId, setInlineId] = useState<string | null>(null);
  const [dLong, setDLong] = useState('');
  const [dShort, setDShort] = useState('');
  const [savingInline, setSavingInline] = useState(false);

  const openNew = () => { setLinkOpen(false); setPopupMax(false); setDlgShowCountry(false); setDlg(null); setSLong(''); setSShort(''); setSCountry(countryId ?? ''); setSErr(''); setStagedCities([]); };
  const openEditRow = (r: StateRow, fromLink = false) => {
    setLinkOpen(false); setPopupMax(false); setDlgShowCountry(fromLink); setDlg(r); setSLong(r.longName ?? ''); setSShort(r.shortName ?? ''); setSCountry(r.countryId ?? countryId ?? ''); setSErr('');
    // Editing a staged state → reload its staged cities; a saved state uses the live City grid.
    setStagedCities(staging ? (staged.find((s) => s.key === r.id)?.cities ?? []) : []);
  };
  const close = () => { setDlg(undefined); setStagedCities([]); };

  const { data: countryOpts = [] } = useStateCountries();   // Country lookup in the State popup
  // A real (saved) state being edited (vs New / staged) — drives the live City
  // grid and a server update instead of an in-memory stage.
  const dlgIsReal = !!dlg?.id && allStates.some((s) => s.id === dlg!.id);

  const startInline = (r: StateRow) => { setInlineId(r.id); setDLong(r.longName ?? ''); setDShort(r.shortName ?? ''); };
  const cancelInline = () => { setInlineId(null); setDLong(''); setDShort(''); };
  const saveInline = async (r: StateRow) => {
    if (!dLong.trim()) { notify.warning('State must not be empty.'); return; }
    setSavingInline(true);
    try {
      const long = dLong.trim(); const short = dShort.trim() || null;
      const sid = realStateId(r.id);
      if (staging) {
        // Staged states are real dbo.CustomState rows (persisted on create) —
        // rename the row, keeping its current Country (the PUT is a full replace).
        await saveState.mutateAsync({ id: sid, longName: long, shortName: short, countryId: countryOf(sid) });
        onStaged?.(staged.map((s) => (s.key === r.id ? { ...s, name: long, shortName: short } : s)));
      } else {
        await saveState.mutateAsync({ id: sid, longName: long, shortName: short, countryId: countryId! });
      }
      cancelInline();
    } catch (e) { notify.error(errMessage(e)); }
    finally { setSavingInline(false); }
  };

  const submit = async () => {
    if (!sLong.trim()) { setSErr('State must not be empty.'); return; }
    setSErr('');
    const long = sLong.trim();
    const short = sShort.trim() || null;
    if (staging && !dlgIsReal) {
      // GoLive persists a new State to dbo.CustomState IMMEDIATELY (only the Country
      // link is deferred to the parent save), so it is a real master row visible
      // everywhere — the Link lookup included. AI kept it in memory only, so a state
      // added on an unsaved country never appeared anywhere.
      try {
        if (dlg) {
          const sid = realStateId(dlg.id);
          await saveState.mutateAsync({ id: sid, longName: long, shortName: short, countryId: sCountry || countryOf(sid) });
          onStaged?.(staged.map((s) => (s.key === dlg.id ? { ...s, name: long, shortName: short, cities: stagedCities } : s)));
        } else {
          // NOTE: useSaveState resolves to the STATE ID string (not the axios
          // response) — unwrapping it as a response yields undefined, which left the
          // staged item without its Oid and made the later rename PUT target the
          // staged key instead of the real row.
          const sid = (await saveState.mutateAsync({ longName: long, shortName: short, countryId: sCountry || null })) ?? undefined;
          // Commit cities staged on this brand-new state (and each city's areas).
          if (sid && stagedCities.length) {
            for (const sc of stagedCities) {
              let cityId = sc.cityId;
              if (cityId) await api.put(`/api/v1/cities/${cityId}`, { cityName: sc.name, stateId: sid });
              else {
                const cres = await api.post('/api/v1/cities', { cityName: sc.name, stateId: sid });
                cityId = (cres as { data?: { data?: { id?: string } } })?.data?.data?.id;
              }
              if (cityId && sc.areas?.length) {
                for (const a of sc.areas) {
                  if (a.areaId) await api.post(`/api/v1/cities/${cityId}/areas/link`, { areaId: a.areaId });
                  else await api.post(`/api/v1/cities/${cityId}/areas`, { areaName: a.name });
                }
              }
            }
          }
          onStaged?.([...staged, { key: newKey(), name: long, shortName: short, stateId: sid, cities: stagedCities }]);
        }
        close();
      } catch (e) { setSErr(errMessage(e)); }
      return;
    }
    const wasNew = !dlgIsReal;   // editing a real state = server UPDATE (keep its country); else CREATE under this country
    await runSave({
      save: async () => {
        // useSaveState resolves to the STATE ID string, not the axios response.
        const savedId = await saveState.mutateAsync({ id: dlgIsReal ? dlg!.id : undefined, longName: long, shortName: short, countryId: sCountry || countryId || null });
        const newStateId = dlgIsReal ? dlg!.id : savedId;
        // Commit any cities staged on this new state (create new / re-assign existing),
        // then commit each new city's staged areas — the full State → City → Area chain.
        if (wasNew && newStateId && stagedCities.length) {
          for (const sc of stagedCities) {
            let cityId = sc.cityId;
            if (cityId) {
              await api.put(`/api/v1/cities/${cityId}`, { cityName: sc.name, stateId: newStateId });
            } else {
              const cres = await api.post('/api/v1/cities', { cityName: sc.name, stateId: newStateId });
              cityId = (cres as { data?: { data?: { id?: string } } })?.data?.data?.id;
            }
            if (cityId && sc.areas?.length) {
              for (const a of sc.areas) {
                if (a.areaId) await api.post(`/api/v1/cities/${cityId}/areas/link`, { areaId: a.areaId });
                else await api.post(`/api/v1/cities/${cityId}/areas`, { areaName: a.name });
              }
            }
          }
        }
      },
      successMessage: 'Saved successfully.', onSaved: () => close(), onError: (e) => notify.error(errMessage(e)),
    });
  };

  const openLink = () => { setDlg(undefined); setPopupMax(false); setLinkSel(new Set()); setLinkSearch(""); setLinkOpen(true); };
  const linkRows = linkable.filter((s) => { const q = linkSearch.trim().toLowerCase(); return !q || (s.longName ?? '').toLowerCase().includes(q); });
  const doLink = async () => {
    if (linkSel.size === 0) { notify.warning('Select at least one state to link.'); return; }
    if (staging) {
      // Skip states already staged onto this country — re-selecting one is a no-op.
      const add = linkable
        .filter((s) => linkSel.has(s.id) && !isLinked(s.id))
        .map((s) => ({ key: newKey(), name: s.longName ?? '', shortName: s.shortName ?? null, stateId: s.id }));
      onStaged?.([...staged, ...add]);
      setLinkOpen(false); setLinkSel(new Set());
      return;
    }
    setBusy(true);
    try {
      for (const id of linkSel) {
        const s = linkable.find((x) => x.id === String(id));
        if (s) await saveState.mutateAsync({ id: s.id, longName: s.longName ?? '', shortName: s.shortName ?? null, countryId: countryId! });
      }
      setLinkOpen(false); setLinkSel(new Set());
    } catch (e) { notify.error(errMessage(e)); }
    finally { setBusy(false); }
  };

  const doUnlink = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one state to unlink.'); return; }
    if (staging) {
      onStaged?.(staged.filter((s) => !selectedIds.has(s.key)));
      setSelectedIds(new Set());
      return;
    }
    const ok = await confirm({
      title: 'Unlink', tone: 'danger', confirmLabel: 'Unlink',
      message: `Remove ${selectedIds.size} state${selectedIds.size === 1 ? '' : 's'} from this country? (The state itself is kept.)`,
    });
    if (!ok) return;
    try {
      for (const id of selectedIds) {
        const s = mine.find((x) => x.id === String(id));
        if (s) await saveState.mutateAsync({ id: s.id, longName: s.longName ?? '', shortName: s.shortName ?? null, countryId: null });
      }
      setSelectedIds(new Set());
    } catch (e) { notify.error(errMessage(e)); }
  };

  const cols: AppColumn<StateRow>[] = [
    {
      field: 'inlineedit', header: '', sortable: false, filterable: false, width: 44, align: 'center',
      // GoLive shows BOTH pencils on EVERY State row — saved or brand-new country.
      render: (r) => (
        inlineId === r.id ? (
          <span className="pts-rowedit" onClick={(e) => e.stopPropagation()}>
            <button className="pts-icon-btn save" title="Save" disabled={savingInline} onClick={() => void saveInline(r)}><i className="ti ti-device-floppy" /></button>
            <button className="pts-icon-btn cancel" title="Cancel" disabled={savingInline} onClick={cancelInline}><i className="ti ti-arrow-back-up" /></button>
          </span>
        ) : (
          <button className="pts-icon-btn edit" title="Inline edit" onClick={(e) => { e.stopPropagation(); startInline(r); }}><i className="ti ti-edit" /></button>
        )
      ),
    },
    {
      field: 'editopen', header: '', sortable: false, filterable: false, width: 44, align: 'center',
      render: (r) => inlineId === r.id ? null : (
        <button className="pts-icon-btn edit" title="Open full edit screen" onClick={(e) => { e.stopPropagation(); openEditRow(r); }}><i className="ti ti-external-link" /></button>
      ),
    },
    {
      field: 'longName', header: 'State', minWidth: 240, text: (r) => r.longName ?? '', compare: (a, b) => (a.longName ?? '').localeCompare(b.longName ?? '', undefined, { sensitivity: 'base' }),
      render: (r) => inlineId === r.id ? (
        <input className="pts-input" autoFocus value={dLong} disabled={savingInline} onClick={(e) => e.stopPropagation()}
          onChange={(e) => setDLong(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void saveInline(r); if (e.key === 'Escape') cancelInline(); }} />
      ) : (<span>{r.longName ?? ''}</span>),
    },
    {
      field: 'shortName', header: 'ShortName', minWidth: 160, sortable: false, text: (r) => r.shortName ?? '',
      render: (r) => inlineId === r.id ? (
        <input className="pts-input" value={dShort} disabled={savingInline} onClick={(e) => e.stopPropagation()} onChange={(e) => setDShort(e.target.value)} />
      ) : (<span>{r.shortName ?? ''}</span>),
    },
  ];
  // LIMS `CustomState_LookupListView` (the Link popup): per-row edit pencil + a
  // single "State" column (ShortName is only in the embedded nested grid).
  const linkCols: AppColumn<StateRow>[] = [
    {
      field: 'edit', header: '', sortable: false, filterable: false, width: 44, align: 'center',
      // GoLive DISABLES the edit pencil for a state already in this country's list.
      render: (r) => {
        const linked = isLinked(r.id);
        return (
          <button className="pts-icon-btn edit" disabled={linked}
                  title={linked ? 'Already added to this country — edit it from the State grid' : 'Edit'}
                  style={linked ? { opacity: 0.35, cursor: 'not-allowed' } : undefined}
                  onClick={(e) => { e.stopPropagation(); if (linked) return; openEditRow(r, true); }}>
            <i className="ti ti-edit" />
          </button>
        );
      },
    },
    { field: 'longName', header: 'State', minWidth: 300, text: (r) => r.longName ?? '', compare: (a, b) => (a.longName ?? '').localeCompare(b.longName ?? '', undefined, { sensitivity: 'base' }) },
  ];

  return (
    <div className="acc-subgrid">
      <div className="acc-sub-title">State</div>
      <div className="acc-sub-toolbar">
        <button className="ca-btn" title="New" onClick={openNew} disabled={noCountry}><i className="ti ti-plus" /> New</button>
        <button className="ca-btn" title="Link" onClick={openLink} disabled={noCountry}><i className="ti ti-link" /> Link</button>
        <button className="ca-btn" title="Unlink" onClick={() => void doUnlink()} disabled={noCountry || selectedIds.size === 0}><i className="ti ti-unlink" /> Unlink</button>
        {/* GoLive keeps the toolbar Edit DISABLED — a state is edited from its row
            pencils (inline / open), not the toolbar; only Unlink enables on select. */}
        <button className="ca-btn" title="Edit a state from its row pencil" disabled><i className="ti ti-edit" /> Edit</button>
        <ExportMenu triggerClassName="ca-btn" filename="State" headers={['State', 'ShortName']} rows={rows.map((s) => [s.longName ?? '', s.shortName ?? ''])} />
      </div>
      {noCountry && <div className="acc-sub-hint">Save the country first, then add or link its states.</div>}

      <AppDataGrid<StateRow>
        rows={rows} columns={cols} getRowId={(r) => r.id}
        selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds} selectedRowId={inlineId ?? dlg?.id ?? null}
        initialSort={{ key: 'longName', dir: 'asc' }} loading={!staging && isFetching} emptyText="No data to display"
        defaultPageSize={20}
      />

      {/* "State" detail popup — State* + Short Name + nested City grid + OK / Cancel
          (legacy CustomState_DetailView). */}
      {dlg !== undefined && (
        <div className="acc-overlay" onMouseDown={(e) => { e.stopPropagation(); close(); }}>
          <div className={`acc-dialog acc-dialog-md${popupMax ? ' acc-dialog-max' : ''}`} style={popupMaxStyle} onMouseDown={(e) => e.stopPropagation()}>
            <div className="acc-dialog-hdr">
              <span>{dlg ? `${dlg.longName ?? ''} - State` : 'State'}</span>
              <span className="acc-dialog-acts">
                <i className={`ti ${popupMax ? 'ti-minimize' : 'ti-maximize'} acc-dialog-x`} title={popupMax ? 'Restore' : 'Maximize'} onClick={() => setPopupMax((m) => !m)} />
                <i className="ti ti-x acc-dialog-x" onClick={close} />
              </span>
            </div>
            <div className="acc-dialog-body">
              <div className="acc-field-h">
                <label className="acc-label acc-label-req">State<span className="req">*</span></label>
                <div className="acc-fh-control">
                  <input className="acc-input" autoFocus value={sLong} onChange={(e) => { setSLong(e.target.value); if (sErr) setSErr(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }} />
                  {sErr && <div className="acc-err">{sErr}</div>}
                </div>
              </div>
              <div className="acc-field-h">
                <label className="acc-label">Short Name</label>
                <div className="acc-fh-control">
                  <input className="acc-input" value={sShort} onChange={(e) => setSShort(e.target.value)} />
                </div>
              </div>
              {dlgShowCountry && (
                <div className="acc-field-h">
                  <label className="acc-label">Country</label>
                  <div className="acc-fh-control">
                    {/* Country lookup: dropdown + erase (clear) icon — LIMS CustomState.Country.
                        Shown only for a standalone State detail (link-picker pencil); hidden
                        when adding/editing within this country's nested grid. */}
                    <div className="acc-lookup">
                      <select className="acc-input" value={sCountry} onChange={(e) => setSCountry(e.target.value)}>
                        <option value="">N/A</option>
                        {countryOpts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                      <button className="acc-lookup-btn" title="Erase" disabled={!sCountry} onClick={() => setSCountry('')}><i className="ti ti-eraser" /></button>
                    </div>
                  </div>
                </div>
              )}
              {/* City child collection — State → City. A real (saved) state shows live
                  cities; a New / staged state stages them in memory until save. */}
              <StateCitiesPanel stateId={dlgIsReal ? dlg!.id : undefined} staged={stagedCities} onStaged={setStagedCities} />
            </div>
            <div className="acc-dialog-ftr">
              <button className="ca-btn ca-btn-solid" onClick={() => void submit()} disabled={saveState.isPending}><span className="tb-i">💾</span> OK</button>
              <button className="ca-btn" onClick={close}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* "Link" picker — checkbox grid of existing states to assign to this country. */}
      {linkOpen && (
        <div className="acc-overlay" onMouseDown={(e) => { e.stopPropagation(); setLinkOpen(false); }}>
          <div className={`acc-dialog acc-dialog-pick${popupMax ? ' acc-dialog-max' : ''}`} style={popupMaxStyle} onMouseDown={(e) => e.stopPropagation()}>
            {/* Every popup carries BOTH maximize/restore and close (GoLive chrome). */}
            <div className="acc-dialog-hdr">
              <span>State</span>
              <span className="acc-dialog-acts">
                <i className={`ti ${popupMax ? 'ti-minimize' : 'ti-maximize'} acc-dialog-x`} title={popupMax ? 'Restore' : 'Maximize'} onClick={() => setPopupMax((m) => !m)} />
                <i className="ti ti-x acc-dialog-x" onClick={() => setLinkOpen(false)} />
              </span>
            </div>
            <div className="acc-dialog-body">
              <div className="acc-pick-search"><div className="acc-search"><i className="ti ti-search" />
                <input placeholder="Search…" autoFocus value={linkSearch} onChange={(e) => setLinkSearch(e.target.value)} />
                {linkSearch && <i className="ti ti-x acc-search-x" onClick={() => setLinkSearch('')} />}
              </div></div>
              <AppDataGrid<StateRow>
                rows={linkRows} columns={linkCols} getRowId={(r) => r.id}
                selectable selectedIds={linkSel} onSelectionChange={setLinkSel}
                initialSort={{ key: 'longName', dir: 'asc' }} emptyText="No states available to link."
                defaultPageSize={20}
              />
            </div>
            <div className="acc-dialog-ftr">
              <button className="ca-btn ca-btn-solid" disabled={busy || linkSel.size === 0} onClick={() => void doLink()}>OK</button>
              <button className="ca-btn" disabled={busy} onClick={() => setLinkOpen(false)}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
